import { useState } from 'react';
import { Alert, Button, Col, Divider, Drawer, Form, message, Row, Select, Space, Upload } from 'antd';
import { drawerWidth, formItemLayout, MAX_FILE_SIZE_MB } from '../../../utils/constants';
import { useTranslation } from 'react-i18next';
import LocalfilesServices from '../../../services/localFilesServices';
import { SaveOutlined, UploadOutlined } from '@ant-design/icons';
import { useUserAuth } from '../../../context/UserContext';
import { errorAlertDisplay } from '../../../utils/functions';
import { LocalFile, NewLocalFileRequest, UserLocalFiles } from './localFiles.types';

const NewLocalFileLog = ({ open, onClose, getLocalFilesLogs, localFilesList, userLocalFilesList, setActiveTab }: { open: boolean, onClose: () => void, getLocalFilesLogs: () => void, localFilesList: LocalFile[], userLocalFilesList: UserLocalFiles[], setActiveTab: (tab: string) => void }) => {
    const { t } = useTranslation();
    const [loadingButton, setLoadingButton] = useState(false);
    const [form] = Form.useForm();
    const [selectedLocalFileId, setSelectedLocalFileId] = useState<string | null>(null);
    const [selectedExtension, setSelectedExtension] = useState<string | null>(null);
    const { user } = useUserAuth();

    const saveData = async () => {
        const values = form.getFieldsValue();
        const fileList = values.file;
        if (!fileList || fileList.length === 0) {
            message.error(t('messages.uploadFileEmpty'));
            return;
        }

        const file = fileList[0].originFileObj;
        const formData = new FormData();
        formData.append('LocalFile', file);
        formData.append('LocalFileID', values.localFileID);
        formData.append('UserID', user.id);

        setLoadingButton(true);
        try {
            const respData = await LocalfilesServices.addLocalFile(formData);
            if (respData.success) {
                message.success(t('messages.success'));
                getLocalFilesLogs();
                onClose();
                setActiveTab('2');
            }
            else {
                errorAlertDisplay(respData);
            }
        } catch (error) {
            message.error(t('messages.errorAccured'));
        }
        finally {
            setLoadingButton(false);
        }
    }

    const normFile = (e: any) => {
        if (Array.isArray(e)) {
            return e;
        }
        return e?.fileList;
    };

    const normalizeExtension = (ext: string) => {
        if (!ext) return null;
        let e = String(ext).trim().toLowerCase();
        if (!e.startsWith('.')) e = `.${e}`;
        // guard against comma-separated or spaced extensions; take first token
        if (e.includes(',')) e = e.split(',')[0].trim();
        if (e.includes(' ')) e = e.split(' ')[0].trim();
        return e;
    };

    const beforeUpload = (file: any) => {
        const fileName = file.name.toLowerCase();

        if (selectedExtension) {
            if (!fileName.endsWith(selectedExtension)) {
                message.error(`${t('localFiles.onlyExtensionAllowed')} ${selectedExtension} ${t('localFiles.only')}`);
                return Upload.LIST_IGNORE;
            }
        }

        const isLt50MB = file.size / 1024 / 1024 <= MAX_FILE_SIZE_MB;
        if (!isLt50MB) {
            message.error(t('messages.uploadSizeLimit'));
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const onCloseDrawer = () => {
        form.resetFields();
        setSelectedExtension(null);
        setSelectedLocalFileId(null);
        onClose();
    }

    const onLocalFileSelect = (value: string) => {
        setSelectedLocalFileId(value);
        const selected = localFilesList.find(f => f.id === value);
        const ext = normalizeExtension(selected?.fileExtension || '');
        setSelectedExtension(ext || null);
        // reset any previously selected file when changing local file
        form.setFieldsValue({ file: [] });
    }

    return (
        <Drawer
            title={t('localFiles.uploadLocalFile')}
            width={drawerWidth}
            onClose={onCloseDrawer}
            destroyOnClose={true}
            open={open}
            extra={
                <Space className="drawerButtons">
                    <Button key="close" onClick={onCloseDrawer}>{t('general.close')}</Button>
                </Space>
            }
        >
            <Form
                id="newLocalfile"
                layout="vertical"
                form={form}
                {...formItemLayout}
                onFinish={saveData}
            >
                <Row gutter={16}>
                    <Col xs={{ span: 24 }} sm={{ span: 12 }} md={{ span: 12 }} lg={{ span: 24 }}>
                        <Form.Item
                            label={t('localFiles.localFileType')}
                            name="localFileID"
                            rules={[{ required: true, message: t('messages.requiredField') }]}
                        >
                            <Select
                                onChange={onLocalFileSelect}
                                options={userLocalFilesList.map(file => ({
                                    label: file.localFileName,
                                    value: file.localFileID
                                }))}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <Form.Item
                            label={t('general.file')}
                            name="file"
                            valuePropName="fileList"
                            getValueFromEvent={normFile}
                            rules={[{ required: true, message: t('messages.requiredField') }]}
                        >
                            <Upload
                                accept={selectedExtension || ''}
                                maxCount={1}
                                beforeUpload={beforeUpload}
                                showUploadList={true}
                                disabled={!selectedLocalFileId}
                                customRequest={({ file, onSuccess }) => {
                                    setTimeout(() => {
                                        onSuccess?.("ok");
                                    }, 500);
                                }}
                            >
                                <Button icon={<UploadOutlined />} disabled={!selectedLocalFileId}>
                                    {t('order.selectFile')}
                                </Button>
                            </Upload>
                        </Form.Item>
                    </Col>

                    {selectedExtension && (
                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                            <Alert type="warning" message={`${t('localFiles.onlyExtensionAllowed')} ${selectedExtension} ${t('localFiles.only')}`} showIcon />
                        </Col>
                    )}

                    <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }}>
                        <Divider />
                        <Button type="default" block htmlType="submit" icon={<SaveOutlined />} loading={loadingButton}>{t('general.validateAndSave')}</Button>
                    </Col>
                </Row>
            </Form>
        </Drawer>
    );
}
export default NewLocalFileLog;